import { cn } from '@/shared/utils/cn.ts';
import { ChevronRightIcon } from '@schuettflix/planum-icons-react';
import { ReactNode, forwardRef } from 'react';

export type PanelInputButtonProps = Pick<React.ComponentPropsWithoutRef<'button'>, 'onClick' | 'disabled'> & {
    label: string;
    className?: string;
    value?: ReactNode;
    isHighlighted?: boolean;
    leadingSlot?: React.ReactNode;
    isInvalid?: boolean;
    errorMessage?: string;
    [key: `data-${string}`]: string | boolean | number | undefined;
};

export const PanelInputButton = forwardRef<HTMLButtonElement, PanelInputButtonProps>(
    (
        { label, className, value, onClick, disabled, isHighlighted, leadingSlot, isInvalid, errorMessage, ...rest },
        ref
    ) => {
        return (
            <div className="flex flex-col gap-2">
                {value && typeof value !== 'string' ? (
                    <button
                        ref={ref}
                        type="button"
                        title={label}
                        onClick={onClick}
                        className={cn(
                            'group flex cursor-pointer items-center gap-2 overflow-hidden rounded border p-4 text-left focus:outline-none disabled:cursor-not-allowed',
                            isHighlighted
                                ? 'bg-surface-active border-dark text-dark'
                                : 'bg-surface focus:border-active',
                            { 'hover:border-hovered': !disabled },
                            { 'border-critical': isInvalid },
                            className
                        )}
                        disabled={disabled}
                        data-highlighted={isHighlighted ? 'true' : undefined}
                        {...rest}
                    >
                        <span className={cn('font-copy-md flex flex-1 overflow-hidden', isHighlighted && 'text-dark')}>
                            {value}
                        </span>
                        <span className="block flex-none">
                            <ChevronRightIcon monocrome />
                        </span>
                    </button>
                ) : (
                    <button
                        ref={ref}
                        type="button"
                        title={label}
                        onClick={onClick}
                        className={cn(
                            'flex h-12 cursor-pointer items-center gap-2 overflow-hidden rounded border px-4 focus:outline-none disabled:cursor-not-allowed',
                            isHighlighted
                                ? 'bg-surface-active border-dark text-dark'
                                : 'bg-surface focus:border-active',
                            { 'hover:border-hovered': !disabled },
                            { 'border-critical': isInvalid },
                            className
                        )}
                        data-highlighted={isHighlighted ? 'true' : undefined}
                        disabled={disabled}
                        {...rest}
                    >
                        {leadingSlot && <span className="block flex-none">{leadingSlot}</span>}
                        <span className="font-copy-md flex min-w-0 flex-1 text-left">
                            {value ? (
                                <span className={cn('truncate', isHighlighted && 'text-dark')}>{value}</span>
                            ) : (
                                <span className={cn('truncate', isHighlighted ? 'text-dark' : 'text-subdued')}>
                                    {label}
                                </span>
                            )}
                        </span>
                        <span className="block flex-none">
                            <ChevronRightIcon monocrome />
                        </span>
                    </button>
                )}
                {isInvalid && <div className="text-critical font-copy-sm">{errorMessage}</div>}
            </div>
        );
    }
);
